/**
 * Composants UI génériques du design system (sans logique métier).
 * Réutilisés partout : Button, Tag, Card, SegmentedControl, EmptyState, Skeleton, Spinner.
 */
import { Loader2 } from 'lucide-react';

/** Bouton contour (variantes primary/secondary/ghost). */
export function Button({ variant = 'secondary', className = '', children, ...props }) {
  const cls = { primary: 'vd-btn-primary', secondary: 'vd-btn-secondary', ghost: 'vd-btn-ghost' }[variant];
  return (
    <button className={`vd-btn ${cls} ${className}`} {...props}>
      {children}
    </button>
  );
}

/** Petite étiquette (neutre, accent, ou remplie). */
export function Tag({ tone = 'neutral', className = '', children, ...props }) {
  const cls = { neutral: '', accent: 'vd-tag-accent', fill: 'vd-tag-fill' }[tone];
  return (
    <span className={`vd-tag ${cls} ${className}`} {...props}>
      {children}
    </span>
  );
}

/** Carte bordée sans remplissage. */
export function Card({ className = '', children, ...props }) {
  return (
    <div className={`vd-card ${className}`} {...props}>
      {children}
    </div>
  );
}

/** Contrôle segmenté pour 2 à 5 options. */
export function SegmentedControl({ options, value, onChange, testId }) {
  return (
    <div className="inline-flex rounded-md border border-hair overflow-hidden" data-testid={testId}>
      {options.map((o) => (
        <button
          key={o.value}
          data-testid={`${testId}-${o.value}`}
          onClick={() => onChange(o.value)}
          className={`px-3 h-8 text-[13px] transition-colors border-r border-hair last:border-r-0 ${
            value === o.value ? 'bg-gold100 text-gold800' : 'text-n600 hover:bg-surface'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** État vide : message + action optionnelle. */
export function EmptyState({ title, description, action, testId }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center" data-testid={testId}>
      <p className="font-title text-2xl text-ink">{title}</p>
      {description && <p className="text-sm text-n600 mt-1 max-w-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

/** Bloc squelette de chargement. */
export function Skeleton({ className = '' }) {
  return <div className={`animate-pulse bg-surface rounded ${className}`} />;
}

/** Indicateur de chargement centré. */
export function Spinner({ label }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-n600 text-sm">
      <Loader2 className="w-4 h-4 animate-spin" /> {label || 'Chargement…'}
    </div>
  );
}

/** En-tête de page : kicker + titre Cormorant. */
export function PageTitle({ kicker, title, className = '' }) {
  return (
    <div className={className}>
      {kicker && <p className="kicker mb-1">{kicker}</p>}
      <h1 className="font-title text-4xl leading-none text-ink">{title}</h1>
    </div>
  );
}
